import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Radar, ChevronRight, BellPlus, Car, Shield, FileText, Receipt,
  HeartPulse, GraduationCap, CreditCard, Home, Utensils, Sparkles,
} from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import { DOCUMENT_CATEGORIES } from "../data/documentsData";
import {
  getRenewalRadarItems, getNextRenewalItems, getRenewalRadarSummary,
  createDocumentReminderTask, shouldStartRenewalPrep, hasExistingPrepTask,
  buildRenewalPrepTask,
} from "../utils/documentUtils";

/* ════════════════════════════════════════════════════════════════════════
   RENEWAL RADAR
   - `compact`  : 1-line summary + top 3 chips (Today dashboard).
   - `wall`     : 1 hero renewal + count chip (Wall Display).
   - default    : full radar with filter chips + list.
   ════════════════════════════════════════════════════════════════════════ */

const CATEGORY_ICON = {
  Vehicle:   Car,
  Insurance: Shield,
  Bills:     Receipt,
  Receipts:  Receipt,
  Medical:   HeartPulse,
  School:    GraduationCap,
  IDs:       CreditCard,
  House:     Home,
  Recipes:   Utensils,
  Tax:       FileText,
  Other:     FileText,
};

const FILTERS = [
  { id: "all",       label: "All" },
  { id: "overdue",   label: "Overdue" },
  { id: "thisWeek",  label: "Next 7 days" },
  { id: "thisMonth", label: "Next 30 days" },
  { id: "comingUp",  label: "Next 90 days" },
];

function relativeLabel(daysLeft) {
  if (daysLeft === 0) return "today";
  if (daysLeft === 1) return "tomorrow";
  if (daysLeft < 0)   return `${Math.abs(daysLeft)} day${Math.abs(daysLeft) === 1 ? "" : "s"} overdue`;
  if (daysLeft < 30)  return `in ${daysLeft} days`;
  if (daysLeft < 365) return `in ${Math.round(daysLeft / 7)} wk`;
  return `in ${Math.round(daysLeft / 30)} mo`;
}

function categoryIcon(category) {
  return CATEGORY_ICON[category] || FileText;
}

export function RenewalRadar({
  variant = "full",
  maxItems = 6,
  onOpenDocument,
  onAddRenewal,
}) {
  const { documents, tasks, updateDocument, addTask } = useHomeOSState();
  const [filter, setFilter] = useState("all");

  const allItems = useMemo(
    () => getRenewalRadarItems(documents || [], tasks || [], { includeTasks: false }),
    [documents, tasks]
  );
  const filtered = useMemo(() => {
    if (filter === "all") return allItems;
    return allItems.filter(it => it.urgency.id === filter);
  }, [allItems, filter]);
  const summary = useMemo(
    () => getRenewalRadarSummary(documents || [], tasks || []),
    [documents, tasks]
  );

  /* Top-3 used by compact + wall variants. */
  const top = useMemo(
    () => getNextRenewalItems(documents || [], tasks || [], 3),
    [documents, tasks]
  );

  const handleAddReminder = (item) => {
    if (item.source !== "document") return;
    const doc = item.doc;
    if (!doc) return;
    if (item.hasReminder) {
      toast("Reminder already set for this renewal.");
      return;
    }
    const payload = createDocumentReminderTask(doc, {});
    if (!payload.dueDate) {
      toast.error("Set a reminder or due date on the document first.");
      return;
    }
    const taskId = addTask(payload);
    updateDocument(doc.id, {
      linkedTaskId: taskId,
      reminderDate: doc.reminderDate || doc.dueDate || doc.renewalDate,
      status: "Reminder set",
    });
    toast.success(`Reminder added for ${payload.dueDate}.`);
  };

  const handleStartPrep = (item) => {
    if (item.source !== "document" || !item.doc) return;
    const payload = buildRenewalPrepTask(item.doc);
    const taskId = addTask(payload);
    updateDocument(item.doc.id, {
      linkedTaskId: taskId,
      status: "Reminder set",
      prepStartedAt: new Date().toISOString(),
    });
    toast.success("ARA added this to Today.");
  };

  /* ── Compact (Today dashboard) ──────────────────────────────── */
  if (variant === "compact") {
    if (summary.total === 0) {
      return (
        <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/80 p-4" data-testid="renewal-radar-compact">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl    flex items-center justify-center shadow-sm">
              <Radar className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Renewal radar</p>
              <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50">All clear for the next few months.</p>
            </div>
          </div>
        </div>
      );
    }
    const headline = summary.next
      ? `${summary.next.title} ${relativeLabel(summary.next.daysLeft)}`
      : `${summary.dueSoon} renewal${summary.dueSoon === 1 ? "" : "s"} this month`;
    return (
      <div className="rounded-2xl border border-gray-100 dark:border-slate-700 bg-white dark:bg-slate-800/80 p-4" data-testid="renewal-radar-compact">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-2xl    flex items-center justify-center shadow-sm">
            <Radar className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-gray-400">Renewal radar</p>
            <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate" data-testid="renewal-radar-compact-headline">{headline}</p>
          </div>
          {onOpenDocument && (
            <button
              onClick={() => onOpenDocument(null)}
              className="text-[10px] font-extrabold text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] hover:underline"
              data-testid="renewal-radar-compact-open"
            >
              Open
            </button>
          )}
        </div>
        <div className="space-y-1.5">
          {top.map(it => {
            const Icon = categoryIcon(it.category);
            return (
              <button
                key={it.id}
                onClick={() => onOpenDocument?.(it.doc || null)}
                className={`w-full text-left flex items-center gap-2 rounded-xl border px-2.5 py-1.5 ${it.urgency.row}`}
                data-testid={`renewal-radar-compact-item-${it.id}`}
              >
                <Icon className={`w-3.5 h-3.5 ${it.urgency.text}`} strokeWidth={1.5} />
                <p className="flex-1 min-w-0 text-xs font-extrabold text-gray-900 dark:text-gray-50 truncate">{it.title}</p>
                <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded-full ${it.urgency.chip}`}>{relativeLabel(it.daysLeft)}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  /* ── Wall (single hero + chip count) ────────────────────────── */
  if (variant === "wall") {
    if (summary.total === 0) return null;
    const hero = summary.next;
    const Icon = categoryIcon(hero?.category);
    return (
      <div className="mt-3 pt-3 border-t border-[color:var(--ara-line-soft)] dark:border-violet-700/50" data-testid="renewal-radar-wall">
        <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] mb-1.5 flex items-center gap-1.5">
          <Radar className="w-3 h-3" strokeWidth={3} /> Renewal radar
        </p>
        <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${hero.urgency.row}`}>
          <Icon className={`w-4 h-4 ${hero.urgency.text}`} strokeWidth={1.5} />
          <p className="flex-1 min-w-0 text-base font-extrabold text-gray-900 dark:text-gray-50 truncate">{hero.title}</p>
          <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full ${hero.urgency.chip}`}>{relativeLabel(hero.daysLeft)}</span>
        </div>
        {summary.total > 1 && (
          <p className="mt-1.5 text-[11px] font-bold text-[color:var(--ara-ink-muted)]/80 dark:text-[color:var(--ara-ink-muted)]/80">
            +{summary.total - 1} more renewal{summary.total - 1 === 1 ? "" : "s"} ahead.
          </p>
        )}
      </div>
    );
  }

  /* ── Full radar (Documents page) ────────────────────────────── */
  const list = filtered.slice(0, maxItems);

  return (
    <section className="rounded-[28px]     dark: dark: dark: border border-[color:var(--ara-line-soft)] dark:border-sky-800/60 p-5" data-testid="renewal-radar-full">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="w-11 h-11 rounded-2xl    flex items-center justify-center shadow-sm">
          <Radar className="w-5 h-5 text-white" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)]">Renewal radar</p>
          <h2 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-gray-50 leading-tight">Important renewals coming up</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
            {summary.total === 0
              ? "Nothing on the horizon — beautifully quiet."
              : <>
                  <strong>{summary.dueSoon}</strong> due in the next 30 days · <strong>{summary.counts.comingUp || 0}</strong> coming up · <strong>{summary.counts.later || 0}</strong> later
                </>
            }
          </p>
        </div>
        {onAddRenewal && (
          <button
            onClick={onAddRenewal}
            className="inline-flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-[color:var(--ara-line-soft)] dark:border-sky-700/60 text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] font-extrabold text-xs px-3 py-2 rounded-full hover:shadow-sm"
            data-testid="renewal-radar-add-btn"
          >
            <BellPlus className="w-3.5 h-3.5" strokeWidth={1.5} />
            Add renewal
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1" data-testid="renewal-radar-filters">
        {FILTERS.map(f => {
          const active = filter === f.id;
          const count = f.id === "all" ? summary.total : summary.counts[f.id] || 0;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold border-2 transition ${
                active
                  ? "bg-transparent text-white border-sky-500"
                  : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:border-[color:var(--ara-line-soft)]"
              }`}
              data-testid={`renewal-radar-filter-${f.id}`}
            >
              {f.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-extrabold rounded-full ${active ? "bg-white/25 text-white" : "bg-black/5 dark:bg-white/10"} px-1.5`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--ara-line-soft)] dark:border-sky-800 p-6 text-center mt-2" data-testid="renewal-radar-empty">
          <Radar className="w-6 h-6 text-[color:var(--ara-ink-muted)] mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm font-bold text-gray-500 dark:text-gray-400">
            {filter === "all" ? "Nothing on the radar yet." : "Nothing in this window."}
          </p>
        </div>
      ) : (
        <ul className="mt-1 space-y-2" data-testid="renewal-radar-list">
          {list.map(item => {
            const Icon = categoryIcon(item.category);
            const cat = DOCUMENT_CATEGORIES.find(c => c.id === item.category);
            const isPrepReady = item.source === "document"
              && item.doc
              && shouldStartRenewalPrep(item.doc)
              && !hasExistingPrepTask(item.doc, tasks || []);
            return (
              <li key={item.id}>
                <div
                  className={`flex items-center gap-3 rounded-2xl border ${item.urgency.row} px-3.5 py-2.5`}
                  data-testid={`renewal-radar-item-${item.id}`}
                >
                  <div className={`w-9 h-9 rounded-xl bg-white/85 dark:bg-slate-900/40 flex items-center justify-center ${item.urgency.text} flex-shrink-0`}>
                    <Icon className="w-4.5 h-4.5" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 leading-tight">{item.title}</p>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-extrabold ${item.urgency.chip}`}>
                        {item.urgency.label}
                      </span>
                      {isPrepReady && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-extrabold bg-transparent text-white" data-testid={`renewal-radar-ready-to-start-${item.id}`}>
                          <Sparkles className="w-2.5 h-2.5" strokeWidth={3} /> Ready to start
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 truncate inline-flex items-center gap-1">
                      {(() => { const Icon = categoryIcon(item.category); return <Icon className="w-3 h-3" strokeWidth={2.25} />; })()}
                      <span>{cat?.label || item.category} · {item.dueDate} · {relativeLabel(item.daysLeft)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {isPrepReady && (
                      <button
                        onClick={() => handleStartPrep(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full    hover: hover: text-white text-[10px] font-extrabold"
                        data-testid={`renewal-radar-start-prep-${item.id}`}
                      >
                        <Sparkles className="w-3 h-3" strokeWidth={1.5} /> Start prep
                      </button>
                    )}
                    {!isPrepReady && !item.hasReminder && item.source === "document" && (
                      <button
                        onClick={() => handleAddReminder(item)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-transparent hover:opacity-80 text-white text-[10px] font-extrabold"
                        data-testid={`renewal-radar-add-reminder-${item.id}`}
                      >
                        <BellPlus className="w-3 h-3" strokeWidth={1.5} /> Remind
                      </button>
                    )}
                    {onOpenDocument && item.doc && (
                      <button
                        onClick={() => onOpenDocument(item.doc)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200 text-[10px] font-extrabold"
                        data-testid={`renewal-radar-open-${item.id}`}
                      >
                        Open <ChevronRight className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default RenewalRadar;
